import type { ChatMessage } from './chat-types';
import { Link } from 'react-router';
import { ArrowUpRight } from 'lucide-react';
import { isInternalCtaUrl, isSafeCtaUrl } from 'helpers/announcement-url';
import { ACCENT_TEXT } from '../AuthControls';
import { PRIVACY_URL, TERMS_URL } from '../legal-links';
import './chat.css';

type ChatBubbleProps = { message: ChatMessage };

const PT_SURFACE = 'bg-panel text-foreground rounded-[16px]';

const LEGAL_LINK = `${ACCENT_TEXT} font-medium underline underline-offset-2 hover:no-underline`;

/**
 * The signup consent line. The words are CONSENT_TEXT's, split around the two links;
 * a test pins the rendered text to that constant so the typing time and the bubble
 * cannot drift apart.
 */
const ConsentBubble = () => (
  <div
    className={`pt-bubble-in w-fit max-w-[min(520px,88%)] px-4 py-3 text-body leading-[1.5] ${PT_SURFACE}`}
    data-from="pt"
  >
    By creating an account you approve our{' '}
    <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className={LEGAL_LINK}>
      Terms of Service
    </a>{' '}
    and{' '}
    <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className={LEGAL_LINK}>
      Privacy Policy
    </a>
    .
  </div>
);

/** https://example.com/path becomes example.com/path, which is what a card shows. */
const displayUrl = (url: string): string => url.replace(/^https?:\/\//, '');

type LinkCardProps = { label: string; url: string };

/**
 * Link, not the shared NavLink: NavLink appends the global query string, which is
 * for moving between dashboard screens with a website selected. Here it would both
 * mangle a link that already has a query and carry the signed-in app's state onto
 * a link clicked by a stranger.
 *
 * Refuses an unsafe href even though the API refuses to store one. Rows predate
 * validation changes, and a renderer that is only as safe as the day its row was
 * written is not safe.
 */
const LinkCard = ({ label, url }: LinkCardProps) => {
  if (!isSafeCtaUrl(url)) return null;

  const body = (
    <>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-body text-foreground group-hover:text-accent font-medium transition-colors duration-150">
          {label}
        </span>
        <span className="log-line text-dim truncate">{displayUrl(url)}</span>
      </span>
      <span className="bg-accent flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] text-white">
        <ArrowUpRight size={16} strokeWidth={1.5} aria-hidden="true" />
      </span>
    </>
  );

  const className =
    'group bg-panel border-line flex w-[min(340px,100%)] items-center gap-3 rounded-[16px] border px-4 py-3 no-underline hover:no-underline';

  if (isInternalCtaUrl(url)) {
    return (
      <Link to={url} className={className}>
        {body}
      </Link>
    );
  }

  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className={className}>
      {body}
    </a>
  );
};

export const TypingBubble = () => (
  <div
    className={`pt-bubble-in text-foreground flex w-fit items-center gap-1 px-4 py-[15px] ${PT_SURFACE}`}
    data-from="pt"
    // Silent. As a role="status" it was announced before every message, nested inside
    // the conversation's own live region, so each line was read twice over.
    aria-hidden="true"
  >
    {[0, 1, 2].map((idx) => (
      <span
        key={idx}
        className="pt-typing-dot h-1.5 w-1.5 rounded-full bg-current opacity-50"
        style={{ animationDelay: `${idx * 0.15}s` }}
      />
    ))}
  </div>
);

export const ChatBubble = ({ message }: ChatBubbleProps) => {
  switch (message.kind) {
    case 'date':
      return <span className="micro-label pt-msg-in">{message.label}</span>;

    case 'consent':
      return <ConsentBubble />;

    case 'headline':
      return (
        <div
          className={`pt-bubble-in w-fit max-w-[min(600px,92%)] px-4 pt-3.5 pb-[15px] text-[clamp(20px,1.9vw,24px)] leading-[1.08] font-medium tracking-[-0.034em] [text-wrap:pretty] ${PT_SURFACE}`}
          data-from="pt"
        >
          {message.text}
        </div>
      );

    case 'link':
      return (
        <div className="pt-bubble-in" data-from="pt" data-announcement={message.announcement}>
          <LinkCard label={message.label} url={message.url} />
        </div>
      );

    case 'quote':
      return (
        <figure
          className="pt-bubble-in bg-panel border-line m-0 w-fit max-w-[min(520px,88%)] rounded-[16px] border px-[18px] py-4"
          data-from="pt"
        >
          <blockquote className="text-foreground m-0 text-lede leading-[1.55]">{message.text}</blockquote>
          <figcaption className="border-line mt-3.5 flex items-center gap-2.5 border-t pt-3">
            <span className="bg-field text-foreground log-line flex h-7 w-7 items-center justify-center rounded-full">
              {message.initials}
            </span>
            <span className="text-foreground text-meta font-medium">{message.name}</span>
            <span className="log-line text-dim">{message.role}</span>
          </figcaption>
        </figure>
      );

    case 'text':
      if (message.from === 'me') {
        return (
          <div
            className="pt-bubble-in bg-accent ml-auto w-fit max-w-[72%] rounded-[16px] px-4 py-3 text-body leading-[1.45] text-white"
            data-from="me"
          >
            {message.text}
          </div>
        );
      }

      return (
        <div
          className={`pt-bubble-in w-fit max-w-[min(520px,88%)] px-4 py-3 text-body leading-[1.5] ${PT_SURFACE}`}
          data-from="pt"
        >
          {message.text}
        </div>
      );
  }
};
